import React, { useContext, useEffect, useMemo, useState } from 'react';
import CloseIcon from './CloseIcon';
import { CustomerServiceApiImpl } from './CustomerServiceApiImpl';
import CustomerServiceArticle from './CustomerServiceArticle';
import { CustomerServiceContext } from './CustomerServiceContext';
import CustomerServiceMain from './CustomerServiceMain';
import CustomerServiceSearch from './CustomerServiceSearch';
import { ICustomerService, ISearchResult } from './ICustomerService';
import { ICustomerServiceApi } from './ICustomerServiceApi';
import SearchIcon from './SearchIcon';
import { useTranslation } from 'react-i18next';
import { en, fr } from './i18n';
import Loading from './Loading';

export interface CustomerServiceProps {
    api?: ICustomerServiceApi;
}

export type ViewState = 'main' | 'search' | 'article';

const CustomerService = ({ api = new CustomerServiceApiImpl() }: CustomerServiceProps) => {
    const { i18n, t } = useTranslation('customer-service');
    i18n.addResourceBundle('en', 'customer-service', en, true, true);
    i18n.addResourceBundle('fr', 'customer-service', fr, true, true);

    const ctx = useContext(CustomerServiceContext);

    const [data, setData] = useState<ICustomerService>({
        CategorizedArticles: [],
        FaqArticles: [],
    });

    const [localSearchTerm, setLocalSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [searchResults, setSearchResults] = useState<ISearchResult[]>([]);

    const showClearInputIcon = useMemo(() => {
        return localSearchTerm !== '';
    }, [localSearchTerm]);

    const onSearchClick = async () => {
        ctx.setView('search');
        ctx.setSearchTerm(localSearchTerm);
        const results = await api.searchArticle(localSearchTerm);
        setSearchResults(results);
        window.history.pushState({}, '', `/pages/customer-service/search/${encodeURIComponent(localSearchTerm)}`);
    };

    const onClearInputClick = async () => {
        setLocalSearchTerm('');
        ctx.setSearchTerm('');
        ctx.setView('main');
        setSearchResults([]);
        window.history.pushState({}, '', '/pages/customer-service');
        setLoading(true);
        api.getCustomerService().then((value) => {
            setData(value);
            setLoading(false);
        });
    };

    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            onSearchClick();
        }
    };

    const popStateListener = (e: PopStateEvent) => {
        e.preventDefault();
        window.location.href = window.location.href;
    };

    useEffect(() => {
        window.addEventListener('popstate', popStateListener);
        return () => window.removeEventListener('popstate', popStateListener);
    }, []);

    useEffect(() => {
        const url = window.location.pathname;
        const urlParts = url.split('/');
        const isSearch = urlParts.some((str) => str.includes('search'));
        const lastUrlPart = urlParts[urlParts.length - 1];
        const isMain = lastUrlPart === 'customer-service';

        if (isMain) {
            api.getCustomerService().then((value) => {
                setData(value);
                setLoading(false);
            });
            return;
        }

        if (isSearch) {
            setLocalSearchTerm(decodeURIComponent(lastUrlPart));
            ctx.setSearchTerm(decodeURIComponent(lastUrlPart));
            api.searchArticle(lastUrlPart).then((results) => {
                setSearchResults(results);
                ctx.setView('search');
                setLoading(false);
            });
            return;
        }

        // Article
        api.getCustomerService().then((value) => {
            setData(value);
            ctx.setView('article');
            ctx.setUrlName(lastUrlPart);
            setLoading(false);
        });
    }, []);

    return (
        <div className="tsc-container">
            <div className="customer-service-faq">
                {ctx.view !== 'article' && (
                    <>
                        <h2 className="customer-service-faq__title">{t('mainTitle')}</h2>
                        <div className="customer-service-faq__search">
                            <input
                                className="customer-service-faq__search-input"
                                type="text"
                                placeholder={t('searchHelpCenter')}
                                value={localSearchTerm}
                                onChange={(e) => {
                                    setLocalSearchTerm(e.target.value);
                                    if (e.target.value === '') {
                                        onClearInputClick();
                                    }
                                }}
                                onKeyDown={(e) => handleSearchKeyDown(e)}
                            />
                            {showClearInputIcon && (
                                <button onClick={onClearInputClick}>
                                    <CloseIcon className="customer-service-faq__search-clear-icon" />
                                </button>
                            )}
                            <button onClick={onSearchClick}>
                                <SearchIcon className="customer-service-faq__search-icon" />
                            </button>
                        </div>
                    </>
                )}

                {loading && (
                    <div className="tsc-container">
                        <div className="customer-service-faq__loading">
                            <Loading />
                        </div>
                    </div>
                )}

                {!loading && ctx.view === 'main' && <CustomerServiceMain data={data} api={api} />}

                {!loading && ctx.view === 'search' && <CustomerServiceSearch results={searchResults} />}

                {!loading && ctx.view === 'article' && (
                    <CustomerServiceArticle categorizedArticles={data.CategorizedArticles} api={api} />
                )}
            </div>
        </div>
    );
};

export default CustomerService;
